import { createFileRoute } from '@tanstack/react-router';
import {
  useGetLesson,
  useGetLessons,
  useGetMembersInstructors,
  useGetSpace,
  useUpdateProgress,
} from '@onesource/services';
import { ContentBlockType, LessonProgressStatus } from '@onesource/schemas';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { MdAccessTime, MdCheckCircleOutline, MdClose } from 'react-icons/md';
import Player from '@vimeo/player';
import { useRef, useEffect, useCallback, useState } from 'react';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { capitalize, useTheme } from '@mui/material';
import { useGetByDevice } from '@onesource/components';
import { ContentCategoryBadge } from '#/features/community-hub/components/content-category-badge';
import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';

/**
 * Video Content Route
 * Path: /community-hub/spaces/:id/modules/:moduleId/lessons/:lessonId/video/:contentId
 * Shows a single video content block
 */
export const Route = createFileRoute(
  '/_protected/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId/video/$contentId',
)({
  component: VideoContentRoute,
});

function VideoContentRoute() {
  const { id: spaceId, moduleId, lessonId, contentId } = Route.useParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [hasTrackedPlay, setHasTrackedPlay] = useState(false);
  const { isMobile } = useGetByDevice();
  const theme = useTheme();

  // Data fetching hooks
  const { data: lesson, isLoading: isLoadingLesson, error: lessonError } = useGetLesson(lessonId);
  const { data: module } = useGetSpace(moduleId);
  const { data: lessons } = useGetLessons(moduleId);
  const findParentLesson = lessons?.find(l => l.id === lesson?.parentId);

  // Instructor data
  const { data: instructorsData } = useGetMembersInstructors();

  const { mutate: updateProgress } = useUpdateProgress(lessonId);

  // Find the video content block
  const videoBlock = lesson?.contentBlocks?.find(
    block => block.id === contentId && block.blockType === ContentBlockType.VIMEO,
  );
  const instructor = instructorsData?.items?.find(i => String(i.id) === videoBlock?.instructorId);

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (!viewTrackedRef.current && videoBlock) {
      viewTrackedRef.current = true;
      updateProgress({ status: LessonProgressStatus.IN_PROGRESS, lastBlockId: contentId, logAction: 'VIDEO_VIEW' });
    }
  }, [contentId, videoBlock, updateProgress]);

  // Track video play and update progress
  const onVideoPlay = useCallback(() => {
    if (!hasTrackedPlay) {
      updateProgress({
        status: LessonProgressStatus.IN_PROGRESS,
        lastBlockId: contentId,
      });
      setHasTrackedPlay(true);
    }
  }, [contentId, updateProgress, hasTrackedPlay]);

  // Track video completion
  const onVideoEnd = useCallback(() => {
    updateProgress({
      status: LessonProgressStatus.COMPLETED,
      lastBlockId: contentId,
    });
  }, [contentId, updateProgress]);

  // Mark video as watched manually
  const handleMarkAsWatched = useCallback(() => {
    updateProgress({
      status: LessonProgressStatus.COMPLETED,
      lastBlockId: contentId,
    });
  }, [contentId, updateProgress]);

  //close button handler
  const handleClose = useCallback(() => {
    window.history.back();
  }, []);

  // Setup Vimeo player event listeners
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }

    const player = new Player(iframe);

    player.on('play', onVideoPlay);
    player.on('ended', onVideoEnd);

    return () => {
      player.off('play');
      player.off('ended');
    };
  }, [onVideoPlay, onVideoEnd]);

  // Loading state
  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {lessonError.message}</Alert>
      </Box>
    );
  }

  // Video not found
  if (!videoBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">Video not found</Alert>
      </Box>
    );
  }

  const duration = videoBlock.vimeoDurationSeconds
    ? `${Math.floor(videoBlock.vimeoDurationSeconds / 60)}:${String(videoBlock.vimeoDurationSeconds % 60).padStart(2, '0')}`
    : null;

  return (
    <Box>
      <LessonBreadcrumbs
        spaceId={spaceId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Video"
      />

      {/* Video Player */}
      <Box
        pt={2}
        pb={4}
        sx={{
          px: { xs: 2, md: 6 },
          background: 'white',
          overflow: theme.palette.common.white,
          width: { xs: '100%', md: 'calc(100% - 190px)' },
        }}
      >
        {videoBlock.vimeoId ? (
          <Box>
            {/* Video Header Bar */}
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: 9, md: 16 } }}>
                  {videoBlock.title || 'Video'}
                </Typography>

                {instructor ? <Divider orientation="vertical" flexItem /> : null}

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 400, fontSize: { xs: 9, md: 16 }, color: theme.palette.grey[600] }}
                >
                  {instructor ? `${instructor.firstName} ${instructor.lastName}` : ''}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.5, md: 2 },
                }}
              >
                {videoBlock.contentCategory && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ContentCategoryBadge size="medium" color="primary" category={videoBlock.contentCategory} />

                    <Typography variant="caption" sx={{ color: theme.palette.grey[600], fontSize: '9px' }}>
                      {capitalize(videoBlock.contentCategory.toLowerCase())}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MdAccessTime size={15} color={theme.palette.primary.main} />
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.grey[600], fontSize: '9px', whiteSpace: 'nowrap' }}
                  >
                    {duration} m
                  </Typography>
                </Box>

                {/* Mark as Watched Button */}
                {videoBlock.isPublicViewMarkAndWatched && (
                  <Button
                    variant="text"
                    size="small"
                    disableRipple
                    startIcon={<MdCheckCircleOutline size={12} color={theme.palette.primary.main} />}
                    onClick={handleMarkAsWatched}
                    sx={{
                      color: 'text.primary',
                      fontSize: 9,
                      minWidth: 'auto',
                      '& .MuiButton-icon': {
                        mr: 0.5,
                      },
                    }}
                  >
                    {isMobile ? 'Watched' : 'Mark as Watched'}
                  </Button>
                )}
                {/* close button */}
                <IconButton
                  size="small"
                  onClick={handleClose}
                  sx={{
                    width: 24,
                    height: 24,
                    color: 'text.primary',
                    fontSize: 16,
                    backgroundColor: theme.palette.grey[100],
                  }}
                >
                  <MdClose />
                </IconButton>
              </Box>
            </Box>

            {/* Video Player */}
            <Box
              sx={{
                position: 'relative',
                paddingBottom: '56.25%', // 16:9 aspect ratio
                height: 0,
                overflow: 'hidden',
              }}
            >
              <iframe
                ref={iframeRef}
                src={`https://player.vimeo.com/video/${videoBlock.vimeoId}?autoplay=0`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '13px',
                }}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={videoBlock.title || 'Video'}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: 400,
              bgcolor: 'grey.200',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              maxWidth: 900,
            }}
          >
            <Typography color="text.secondary">Video not available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
